<script setup>
import { computed, ref, watch } from 'vue';

import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import Stat from '@/components/ds/Stat.vue';
import { announce } from '@/composables/useAriaAnnouncer.js';

const props = defineProps({
  value: { type: Number, required: true },
  sparklinePoints: { type: Array, default: () => [] },
  size: { type: String, default: 'hero', validator: (s) => ['hero', 'medium'].includes(s) },
  label: { type: String, default: 'Active sessions' },
  // Cadencia de poll del padre (ms). Debounce del aria-live alineado.
  pollIntervalMs: { type: Number, default: 30_000 }
});

const tickActive = ref(false);
let tickTimer = null;
let lastAnnouncedAt = 0;

watch(
  () => props.value,
  (next, prev) => {
    if (next === prev) return;
    tickActive.value = true;
    if (tickTimer) clearTimeout(tickTimer);
    tickTimer = setTimeout(() => { tickActive.value = false; }, 200);

    const now = Date.now();
    if (now - lastAnnouncedAt >= props.pollIntervalMs) {
      announce(`${next} ${props.label.toLowerCase()}`);
      lastAnnouncedAt = now;
    }
  }
);

const wrapperClasses = computed(() => ['pulse', { 'pulse--ticking': tickActive.value }]);
</script>

<template>
  <div :class="wrapperClasses">
    <Stat
      :value="value"
      :label="label"
      unit="live"
      :size="size"
      class="pulse__anchor"
    />
    <AnalyticsChart
      v-if="sparklinePoints.length > 0"
      type="line"
      :labels="sparklinePoints.map((p) => p.ts)"
      :datasets="[{ label: 'Active sessions', data: sparklinePoints.map((p) => p.value) }]"
      aria-label="Active sessions trend over the last 60 minutes"
      class="pulse__spark"
    />
  </div>
</template>

<style scoped>
.pulse {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.pulse__anchor :deep(.ds-stat__value) {
  transition:
    color var(--motion-base) var(--motion-ease-out),
    font-weight var(--motion-base) var(--motion-ease-out),
    text-shadow var(--motion-base) var(--motion-ease-out);
}

.pulse--ticking :deep(.ds-stat__value) {
  font-weight: var(--fw-bold);
  color: var(--success-icon);
}

@media (prefers-reduced-motion: reduce) {
  .pulse__anchor :deep(.ds-stat__value) {
    transition: none;
  }
}
</style>
